import React, { useMemo, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import type { TooltipProps } from 'recharts';
import { Card } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { WarehouseWorkStatTypeInfo } from '../../graphql/queries/getWorkProgressData';

export interface ScanningDataPoint {
  hour: string; // 格式: "2024-01-01 10:00"
  [key: string]: string | number; // 动态字段，key为legendKey
}

interface ScanningChartProps {
  data: ScanningDataPoint[];
  loading?: boolean;
  statTypes: WarehouseWorkStatTypeInfo[];
}

const ScanningChart: React.FC<ScanningChartProps> = ({ data, loading, statTypes }) => {
  const { t } = useTranslation();
  const [hiddenKeys, setHiddenKeys] = useState<Set<string>>(() => new Set());

  // 颜色数组，用于不同线条
  const colors = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#00ff00', '#0088fe', '#00c49f', '#ffbb28', '#ff8042'];

  // 格式化X轴显示，只显示时间部分
  const formatXAxis = (tickItem: string) => {
    if (!tickItem) return '';
    try {
      const date = dayjs(tickItem, 'YYYY-MM-DD HH:mm');
      if (!date.isValid()) {
        // 如果解析失败，尝试其他格式
        const date2 = dayjs(tickItem);
        if (date2.isValid()) {
          return date2.format('HH:mm');
        }
        return tickItem;
      }
      return date.format('HH:mm');
    } catch (e) {
      return tickItem;
    }
  };

  // 格式化工具提示
  type TooltipFormatter = NonNullable<TooltipProps<number | string, string>['formatter']>;
  type TooltipLabelFormatter = NonNullable<TooltipProps<number | string, string>['labelFormatter']>;

  const formatTooltip: TooltipFormatter = (value, name) => {
    // 根据legendKey获取多语言标签
    const statType = statTypes.find(st => st.legendKey === name);
    const label: string = statType
      ? t(`wms.progress.workProgress.statType.${statType.legendKey}`)
      : String(name ?? '');
    return [value as React.ReactNode, label];
  };

  // 格式化标签
  const formatLabel: TooltipLabelFormatter = (label) => {
    if (typeof label === 'string') {
      return dayjs(label).format('YYYY-MM-DD HH:mm');
    }
    return label as React.ReactNode;
  };

  // 格式化图例
  const formatLegend = (value: string) => {
    const statType = statTypes.find(st => st.legendKey === value);
    return statType ? t(`wms.progress.workProgress.statType.${statType.legendKey}`) : value;
  };

  const handleLegendClick = (data: any) => {
    const key = (data?.dataKey ?? data?.value) as string | undefined;
    if (!key) return;
    toggleLegend(key);
  };

  const legendFormatter = (value: any, entry: any) => {
    const key = (entry?.dataKey ?? value) as string;
    const isHidden = hiddenKeys.has(key);
    const label = formatLegend(key);
    return (
      <span style={{ color: isHidden ? '#999' : undefined, textDecoration: isHidden ? 'line-through' : 'none' }}>
        {label}
      </span>
    );
  };

  const toggleLegend = (key: string) => {
    setHiddenKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // 让 Y 轴只根据当前“可见”的系列计算：把被隐藏系列的数据置空
  const chartData = useMemo(() => {
    if (!hiddenKeys.size) return data;
    return data.map(point => {
      const next: any = { ...point };
      hiddenKeys.forEach(k => {
        next[k] = null;
      });
      return next;
    });
  }, [data, hiddenKeys]);

  // 如果没有数据，显示提示
  if (!loading && (!data || data.length === 0)) {
    return (
      <Card title={t('wms.progress.workProgress.chartTitle')} className="mb-4">
        <div className="h-80 flex items-center justify-center">
          <span className="text-gray-400">{t('components.message.noData')}</span>
        </div>
      </Card>
    );
  }

  return (
    <Card title={t('wms.progress.workProgress.chartTitle')} className="mb-4">
      {loading ? (
        <div className="h-80 flex items-center justify-center">
          <span>{t('components.message.loading')}</span>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={400}>
          <LineChart
            data={chartData}
            margin={{
              top: 5,
              right: 30,
              left: 20,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="hour"
              tickFormatter={formatXAxis}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis />
            <Tooltip
              formatter={formatTooltip}
              labelFormatter={formatLabel}
            />
            <Legend formatter={legendFormatter} onClick={handleLegendClick} />
            {statTypes.map((statType, index) => {
              const isHidden = hiddenKeys.has(statType.legendKey);
              return (
                <Line
                  key={statType.legendKey}
                  type="monotone"
                  dataKey={statType.legendKey}
                  stroke={isHidden ? '#999' : colors[index % colors.length]}
                  strokeOpacity={isHidden ? 0 : 1}
                  strokeWidth={2}
                  dot={isHidden ? false : { r: 4 }}
                  activeDot={isHidden ? false : { r: 6 }}
                />
              );
            })}
          </LineChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
};

export default ScanningChart;

